import { useState, useEffect, useCallback } from "react";
import { Download01, Printer } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { DatePicker } from "@/components/ui/date-picker";
import { getDailyClosing, downloadReportCsv, type DailyClosingData } from "@/api/reports";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(amount);

const formatPaymentMethod = (method: string | null) => {
  if (!method) return "—";
  return method.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function DailyClosingTab() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [data, setData] = useState<DailyClosingData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getDailyClosing(date);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    downloadReportCsv("daily-closing", { date }, `daily-closing-${date}.csv`);
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DatePicker value={date} onChange={setDate} />
        <Button size="md" color="secondary" iconLeading={Download01} onClick={handleExport}>
          Export CSV
        </Button>
        <Button size="md" color="secondary" iconLeading={Printer} onClick={() => window.print()}>
          Print
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-text-tertiary">Loading report...</div>
      ) : !data ? (
        <div className="py-12 text-center text-text-tertiary">Failed to load report</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <SummaryCard label="Total Invoiced" value={formatCurrency(data.summary.totalInvoiced)} color="brand" />
            <SummaryCard label="Paid" value={formatCurrency(data.summary.totalPaid)} sublabel={`${data.summary.countPaid} invoices`} color="green" />
            <SummaryCard label="Pending" value={formatCurrency(data.summary.totalPending)} sublabel={`${data.summary.countPending} invoices`} color="orange" />
            <SummaryCard label="Cash" value={formatCurrency(data.summary.byPaymentMethod.cash)} color="brand" />
            <SummaryCard label="Card" value={formatCurrency(data.summary.byPaymentMethod.card)} color="brand" />
            <SummaryCard label="Bank Transfer" value={formatCurrency(data.summary.byPaymentMethod.bank_transfer)} color="brand" />
          </div>

          {/* Invoice Table */}
          {data.invoices.length === 0 ? (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-8 text-center text-text-tertiary">
              No invoices for {date}
            </div>
          ) : (
            <div className="rounded-xl border border-border-secondary bg-bg-primary overflow-hidden">
              {/* Mobile cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden">
                {data.invoices.map((inv) => (
                  <div key={inv.id} className="rounded-lg border border-border-secondary p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-text-primary">#{inv.invoiceNumber}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                    <p className="text-sm text-text-secondary">{inv.customerName}</p>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="text-text-tertiary">{formatPaymentMethod(inv.paymentMethod)}</span>
                      <span className="font-medium text-text-primary">{formatCurrency(inv.total)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <table className="hidden xl:table w-full text-sm">
                <thead>
                  <tr className="border-b border-border-secondary bg-bg-secondary">
                    <th className="px-6 py-3 text-left font-medium text-text-tertiary">Invoice #</th>
                    <th className="px-6 py-3 text-left font-medium text-text-tertiary">Customer</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Subtotal</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Tax</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Total</th>
                    <th className="px-6 py-3 text-center font-medium text-text-tertiary">Status</th>
                    <th className="px-6 py-3 text-center font-medium text-text-tertiary">Payment</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-border-secondary last:border-0">
                      <td className="px-6 py-4 font-medium text-text-primary">#{inv.invoiceNumber}</td>
                      <td className="px-6 py-4 text-text-secondary">{inv.customerName}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{formatCurrency(inv.subtotal)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{formatCurrency(inv.taxAmount)}</td>
                      <td className="px-6 py-4 text-right font-medium text-text-primary">{formatCurrency(inv.total)}</td>
                      <td className="px-6 py-4 text-center"><StatusBadge status={inv.status} /></td>
                      <td className="px-6 py-4 text-center text-text-secondary">{formatPaymentMethod(inv.paymentMethod)}</td>
                      <td className="px-6 py-4 text-right text-text-tertiary">
                        {new Date(inv.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-bg-secondary font-medium">
                    <td className="px-6 py-3 text-text-primary" colSpan={4}>Total</td>
                    <td className="px-6 py-3 text-right text-text-primary">{formatCurrency(data.summary.totalInvoiced)}</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sublabel, color }: {
  label: string;
  value: string;
  sublabel?: string;
  color?: "green" | "orange" | "brand";
}) {
  const styles = color === "green"
    ? "border-l-green-500 shadow-[inset_4px_0_8px_-4px_rgba(34,197,94,0.4)]"
    : color === "orange"
    ? "border-l-orange-500 shadow-[inset_4px_0_8px_-4px_rgba(249,115,22,0.4)]"
    : "border-l-[var(--color-fg-brand-primary)] shadow-[inset_4px_0_8px_-4px_rgba(21,112,239,0.3)]";

  return (
    <div className={`rounded-xl border border-border-secondary bg-bg-primary p-4 border-l-4 ${styles}`}>
      <p className="text-xs text-text-tertiary mb-1">{label}</p>
      <p className="text-lg font-semibold text-text-primary">{value}</p>
      {sublabel && <p className="text-xs text-text-tertiary mt-0.5">{sublabel}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-700",
    paid: "bg-green-100 text-green-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${colors[status] ?? "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}
